import React, { useState, useEffect, useRef, useCallback } from 'react';
import MobileLayout from '../../../components/layout/MobileLayout';
import { useToast } from '../../../components/ui/Toast';
import { useI18n } from '../../../i18n/I18n';
import FormField from '../../../components/ui/FormField';
import ChainTokenList from '../../../components/ui/ChainTokenList';
import { PaymentSettings } from '../../../types/payment.types';
import { getMerchantPaymentSettings, updateMerchantPaymentSettings } from '../../../services/useAPICaller';
import { toggleChainSelection, toggleTokenSelection } from '../../../hooks/useChainTokenToggle';

export default function WalletView() {
    const { t } = useI18n();
    const [loading, setLoading] = useState(true);
    const [paymentSettings, setPaymentSettings] = useState<PaymentSettings | null>(null);
    const [autosaveStatus, setAutosaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
    const firstRender = useRef(true);

    // Form Data State
    const [formData, setFormData] = useState({
        merchantName: '',
        businessUrl: '',
        merchantLogoUrl: '',
        walletAddress: '',
        selectedChains: [] as string[],
        selectedTokens: [] as string[]
    });

    const toast = useToast();

    // Fetch Settings
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                setLoading(true);
                await getMerchantPaymentSettings((data: PaymentSettings) => {
                    setPaymentSettings(data);

                    if (data) {
                        // Inherit active chains and tokens from settings
                        const activeChains = data.chains?.filter(c => c.active).map(c => c.id.toString()) || [];
                        const activeTokens: string[] = [];

                        data.chains?.forEach(chain => {
                            if (chain.active) {
                                chain.tokens?.forEach(token => {
                                    if (token.active) {
                                        activeTokens.push(`${chain.id}:${token.symbol}`);
                                    }
                                });
                            }
                        });

                        // Attempt to find a "default" wallet address from the first active chain
                        // Or use the one from the first chain if none active
                        const defaultChain = data.chains?.find(c => c.active) || data.chains?.[0];
                        const initialWalletAddress = defaultChain?.publicAddress || '';

                        setFormData({
                            merchantName: data.merchantName || '',
                            businessUrl: data.businessUrl || '',
                            merchantLogoUrl: data.merchantLogoUrl || '',
                            walletAddress: initialWalletAddress,
                            selectedChains: activeChains,
                            selectedTokens: activeTokens
                        });
                    }
                });
            } catch (error) {
                console.error("Error fetching settings:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, []);

    const handleToggleChain = (chainId: string) => {
        setFormData(prev => toggleChainSelection(prev, chainId, paymentSettings?.chains));
    };

    const handleToggleToken = (chainId: string, tokenSymbol: string) => {
        setFormData(prev => toggleTokenSelection(prev, chainId, tokenSymbol));
    };

    // Memoized so the autosave effect below re-arms its debounce only when the
    // saved payload actually changes, not on every render.
    const handleSave = useCallback(async (silent = false) => {
        if (!paymentSettings) return;
        if (silent) setAutosaveStatus('saving');

        try {
            // Clone settings to prepare update payload
            const updatedSettings: PaymentSettings = JSON.parse(JSON.stringify(paymentSettings));

            updatedSettings.merchantName = formData.merchantName;
            updatedSettings.businessUrl = formData.businessUrl;
            updatedSettings.merchantLogoUrl = formData.merchantLogoUrl;

            // Update Chains and Tokens
            updatedSettings.chains.forEach((chain) => {
                const chainIdStr = chain.id.toString();
                const isChainActive = formData.selectedChains.includes(chainIdStr);

                chain.active = isChainActive;

                // Update wallet address for active chains
                if (formData.walletAddress) {
                    chain.publicAddress = formData.walletAddress;
                }

                if (chain.tokens) {
                    chain.tokens.forEach((token) => {
                        const tokenKey = `${chainIdStr}:${token.symbol}`;
                        const isTokenActive = formData.selectedTokens.includes(tokenKey);
                        token.active = isTokenActive;
                    });
                }
            });

            await updateMerchantPaymentSettings(updatedSettings);

            if (silent) {
                setAutosaveStatus('saved');
                setTimeout(() => setAutosaveStatus('idle'), 2000);
            } else {
                toast({ title: t('wallet.settingsSaved'), status: "success" });
            }
        } catch (error) {
            console.error("Error saving settings:", error);
            if (silent) {
                setAutosaveStatus('idle');
            } else {
                toast({ title: t('wallet.errorSaving'), status: "error" });
            }
        }
    }, [paymentSettings, formData, t, toast]);

    // Autosave Effect
    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false;
            return;
        }

        const timeoutId = setTimeout(() => {
            handleSave(true);
        }, 1000);

        return () => clearTimeout(timeoutId);
    }, [formData, handleSave]);

    return (
        <MobileLayout>
            {/* Header */}
            <div className="px-6 lg:px-10 pt-14 lg:pt-10 pb-4 flex justify-between items-center z-10 w-full max-w-md lg:max-w-5xl bg-transparent relative">
                <h1 className="text-2xl font-bold text-txt-heading tracking-tight">{t('wallet.title')}</h1>

                {/* Autosave Indicator */}
                <div className="flex items-center gap-2">
                    {autosaveStatus === 'saving' && (
                        <span className="text-xs text-txt-muted animate-pulse">{t('wallet.saving')}</span>
                    )}
                    {autosaveStatus === 'saved' && (
                        <span className="text-xs text-emerald-400 flex items-center gap-1">
                            <span className="material-symbols-outlined text-[14px]">check</span>
                            {t('wallet.saved')}
                        </span>
                    )}
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 w-full max-w-md lg:max-w-5xl overflow-y-auto no-scrollbar px-6 lg:px-10 pb-32 lg:pb-10 z-10 space-y-6">

                {loading ? (
                    <div className="text-txt-heading text-center mt-10">{t('wallet.loading')}</div>
                ) : (
                    <>
                        {/* Business Details Section */}
                        <div className="space-y-4 lg:grid lg:grid-cols-2 lg:gap-6 lg:space-y-0">
                            <FormField label={t('wallet.businessName')}>
                                <input
                                    type="text"
                                    value={formData.merchantName}
                                    onChange={(e) => setFormData(prev => ({ ...prev, merchantName: e.target.value }))}
                                    placeholder="Ej. Mi Tienda"
                                    className="w-full bg-transparent border-none text-txt-heading text-lg placeholder:text-txt-muted focus:ring-0 focus:outline-none p-0"
                                />
                            </FormField>

                            <FormField label={t('wallet.websiteUrl')}>
                                <input
                                    type="text"
                                    value={formData.businessUrl}
                                    onChange={(e) => setFormData(prev => ({ ...prev, businessUrl: e.target.value }))}
                                    placeholder="https://..."
                                    className="w-full bg-transparent border-none text-txt-heading text-md placeholder:text-txt-muted focus:ring-0 focus:outline-none p-0"
                                />
                            </FormField>

                            <FormField
                                label={t('business.logoUrlOptional')}
                                badge={<span className="text-[10px] text-txt-muted">{t('business.recommendedDimensions')}</span>}
                                className="lg:col-span-2"
                            >
                                <input
                                    type="text"
                                    value={formData.merchantLogoUrl}
                                    onChange={(e) => setFormData(prev => ({ ...prev, merchantLogoUrl: e.target.value }))}
                                    placeholder="https://.../logo.png"
                                    className="w-full bg-transparent border-none text-txt-heading text-md placeholder:text-txt-muted focus:ring-0 focus:outline-none p-0"
                                />
                            </FormField>

                            <FormField
                                label={t('wallet.walletAddress')}
                                badge={<span className="text-[10px] font-bold text-electric-blue bg-electric-blue/10 px-2 py-0.5 rounded-full">{t('wallet.multichain')}</span>}
                                className="lg:col-span-2"
                            >
                                <div className="flex items-center gap-3">
                                    <span className="material-symbols-outlined text-txt-muted text-[24px]">account_balance_wallet</span>
                                    <input
                                        type="text"
                                        value={formData.walletAddress}
                                        onChange={(e) => setFormData(prev => ({ ...prev, walletAddress: e.target.value }))}
                                        placeholder="0x..."
                                        className="w-full bg-transparent border-none text-txt-heading font-mono text-xs placeholder:text-txt-muted focus:ring-0 focus:outline-none p-0"
                                    />
                                </div>
                            </FormField>
                        </div>

                        {/* Networks & Tokens Section */}
                        {paymentSettings?.chains && (
                            <ChainTokenList
                                chains={paymentSettings.chains}
                                selectedChains={formData.selectedChains}
                                selectedTokens={formData.selectedTokens}
                                onToggleChain={handleToggleChain}
                                onToggleToken={handleToggleToken}
                            />
                        )}
                    </>
                )}
            </div>
        </MobileLayout>
    );
}
